/**
 * Structured API failure. Parsed from the application/problem+json body defined in
 * docs/api-contract.md section 2, or synthesised for transport failures (status 0).
 */
export interface ProblemItem {
  index?: number;
  id?: string;
  code?: string;
  current_version?: number;
  paths?: string[];
}

export interface ProblemFieldError {
  path?: string;
  code?: string;
  message?: string;
}

export interface ProblemBody {
  type?: string;
  title?: string;
  status?: number;
  code?: string;
  detail?: string;
  request_id?: string;
  operation_id?: string;
  planned_phase?: string;
  current_version?: number;
  retry_after_seconds?: number;
  errors?: ProblemFieldError[];
  items?: ProblemItem[];
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly requestId: string | undefined;
  readonly operationId: string | undefined;
  readonly plannedPhase: string | undefined;
  readonly retryAfterSeconds: number | undefined;
  readonly problem: ProblemBody;

  constructor(status: number, problem: ProblemBody, fallbackMessage?: string) {
    super(problem.detail ?? problem.title ?? fallbackMessage ?? `Request failed (${status})`);
    this.name = 'ApiError';
    this.status = status;
    this.problem = problem;
    this.code = problem.code ?? (status === 0 ? 'network_error' : `http_${status}`);
    this.requestId = problem.request_id;
    this.operationId = problem.operation_id;
    this.plannedPhase = problem.planned_phase;
    this.retryAfterSeconds = problem.retry_after_seconds;
  }

  /** The operation exists in the contract but the server has not built it yet. */
  get notImplemented(): boolean {
    return this.status === 501 || this.code === 'not_implemented';
  }
  get unauthenticated(): boolean {
    return this.status === 401;
  }
  get forbidden(): boolean {
    return this.status === 403;
  }
  get notFound(): boolean {
    return this.status === 404;
  }
  get unreachable(): boolean {
    return this.status === 0 || this.status === 502 || this.status === 504;
  }
  get versionConflict(): boolean {
    return this.status === 412 || this.code === 'version_conflict';
  }
  get rateLimited(): boolean {
    return this.status === 429;
  }
}

export function isApiError(e: unknown): e is ApiError {
  return e instanceof ApiError;
}

function asProblem(body: unknown): ProblemBody {
  if (body && typeof body === 'object') {
    const b = body as Record<string, unknown>;
    // Tolerate a nested {error:{...}} envelope as well as flat problem+json.
    if (b.error && typeof b.error === 'object') {
      const e = b.error as Record<string, unknown>;
      return {
        code: typeof e.code === 'string' ? e.code : undefined,
        detail: typeof e.message === 'string' ? e.message : undefined,
        request_id: typeof e.request_id === 'string' ? e.request_id : undefined,
      };
    }
    return b as ProblemBody;
  }
  return {};
}

export function toApiError(response: Response | undefined, body: unknown): ApiError {
  const status = response?.status ?? 0;
  const problem = asProblem(body);
  if (!problem.request_id && response) {
    const rid = response.headers.get('X-Request-ID');
    if (rid) problem.request_id = rid;
  }
  if (problem.retry_after_seconds === undefined && response) {
    const ra = Number(response.headers.get('Retry-After'));
    if (Number.isFinite(ra) && ra > 0) problem.retry_after_seconds = ra;
  }
  return new ApiError(status, problem, response?.statusText);
}

export function networkError(cause: unknown): ApiError {
  const err = new ApiError(0, {
    code: 'network_error',
    title: 'API unreachable',
    detail: 'The API did not respond. Check your connection or the API status indicator.',
  });
  (err as { cause?: unknown }).cause = cause;
  return err;
}

/** Short, plain description used by the shared ErrorState. */
export function describeError(e: unknown): { title: string; body: string } {
  if (!isApiError(e)) {
    return { title: 'Something broke in the app', body: e instanceof Error ? e.message : 'Unknown error.' };
  }
  if (e.notImplemented) {
    return {
      title: 'Not built yet',
      body: `The API has this operation in its contract but it is not implemented in this release${
        e.plannedPhase ? ` (planned for ${e.plannedPhase})` : ''
      }.`,
    };
  }
  // Status 0, or a gateway 502/504 without an API problem body (proxy cannot reach the API).
  if (e.status === 0 || (e.unreachable && !e.problem.type))
    return { title: 'API unreachable', body: 'No response from the API. Nothing shown here is current.' };
  if (e.status === 401) return { title: 'Signed out', body: 'Your session ended. Sign in again to continue.' };
  if (e.status === 403) return { title: 'No access', body: 'Your role does not include this. Ask an admin if you need it.' };
  if (e.status === 404) return { title: 'Not found', body: 'This item does not exist or you cannot see it.' };
  if (e.status === 409 && e.code === 'feature_disabled')
    return { title: 'Turned off', body: 'This feature is disabled for this organization.' };
  if (e.status === 412) return { title: 'Changed by someone else', body: 'Reload to see the current version before saving.' };
  if (e.status === 429)
    return {
      title: 'Rate limited',
      body: e.retryAfterSeconds ? `Try again in ${e.retryAfterSeconds} seconds.` : 'Try again shortly.',
    };
  if (e.status === 503) return { title: 'Service unavailable', body: e.message };
  if (e.status >= 500) return { title: 'Server error', body: 'The API failed to answer. Reads can be retried safely.' };
  return { title: 'Request failed', body: e.message };
}
